import { Box, Portal } from '@chronosphereio/chrono-ui';
import { AbsoluteTimeRange } from '@chronosphereio/core';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useResizeObserver from 'use-resize-observer';
import {
  SeriesMenuItemOptions,
  TIME_SERIES_TOOLTIP_MODE_SERIES_LIMIT,
  TimeChartSeriesMapping,
  TimeScale,
  TimeSeriesDataset,
  TimeSeriesOption,
  TooltipMode,
  UnitOptions,
} from '@/core';
import { ChartApi } from '../EChart';
import {
  assembleTransform,
  CursorCoordinates,
  TooltipDynamicContentWrapper,
  TooltipLayout,
  TooltipStaticContentWrapper,
  usePanelTooltipState,
} from '../PanelTooltip';
import { CommonLabelsSection } from './CommonLabelsSection';
import { getTooltipData } from './nearby-series';
import { TimeSeriesTooltipFooter } from './TimeSeriesTooltipFooter';
import { TimeSeriesTooltipHeader } from './TimeSeriesTooltipHeader';
import { EmphasizedSeriesSection, TooltipContent } from './TooltipContent';
import { TooltipContextMenu } from './TooltipContextMenu';
import {
  DEFAULT_MAX_NEARBY_SERIES,
  EMPTY_TOOLTIP_DATA,
  TOOLTIP_HEIGHT_INCREASE_INCREMENT,
  TOOLTIP_NEARBY_SERIES_MAX_HEIGHT,
  TooltipConfig,
} from './tooltip-model';
import { checkUnitPrecision, getEmphasizedSeriesInfo, getTooltipStyles, isEvent, sortTooltipSeries } from './utils';

export interface TimeChartTooltipProps {
  chartApiRef: React.MutableRefObject<ChartApi | undefined>;
  dataset: TimeSeriesDataset[];
  seriesMapping: TimeChartSeriesMapping;
  tooltipConfig: TooltipConfig;
  timeScale: TimeScale;
  timeRange: AbsoluteTimeRange;
  mouseoverSeriesId?: string;
  enablePinning?: boolean;
  pinnedPos?: CursorCoordinates;
  /**
   * The container element that will have the chart tooltip appended to it.
   * By default, the chart tooltip is attached to document.body.
   */
  containerElement?: Element | null;
  onUnpinClick?: () => void;
  unit?: UnitOptions;
  isStacked?: boolean;
  eventSeriesOption?: TimeSeriesOption;
}

export const TimeChartTooltip = memo(function TimeChartTooltip({
  containerElement,
  chartApiRef,
  dataset,
  seriesMapping,
  tooltipConfig,
  timeScale,
  timeRange,
  mouseoverSeriesId,
  enablePinning = true,
  unit,
  onUnpinClick,
  pinnedPos,
  isStacked,
  eventSeriesOption,
}: TimeChartTooltipProps) {
  const transform = useRef<string | undefined>();

  const totalSeries = dataset.length;

  const { maxNearbySeries, toggleTooltipMode } = useUpdateTooltipMode({
    initialMaxNearbySeries: tooltipConfig.maxNearbySeries,
    mode: tooltipConfig.mode,
  });

  // When all series shown in the tooltip, emphasized series can be extracted to a separate section.
  // Clicking the ellipsis can then be used to increase the height of the tooltip.
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState(TOOLTIP_NEARBY_SERIES_MAX_HEIGHT);

  const tooltipState = usePanelTooltipState();
  const { mousePos, activePanelKey } = tooltipState || {};

  const { height, width, ref: tooltipRef } = useResizeObserver();

  const isTooltipPinned = pinnedPos != null && enablePinning;
  const isOverChart = tooltipConfig.panelKey && activePanelKey === tooltipConfig.panelKey;
  const shouldRenderTooltip = isTooltipPinned || isOverChart;

  const chartApi = chartApiRef.current;

  const adjustedUnit = unit !== undefined ? checkUnitPrecision(unit, tooltipConfig.enableTooltipPreciseValues) : unit;

  const { tooltipSeries, total } = shouldRenderTooltip
    ? getTooltipData({
        tooltipConfig,
        maxNearbySeries,
        mousePos,
        dataset,
        seriesMapping,
        eventSeries: eventSeriesOption,
        pinnedPos,
        chartApi,
        timeScale,
        mouseoverSeriesId,
      })
    : { tooltipSeries: EMPTY_TOOLTIP_DATA, total: 0 };

  // Sort before checking whether to extract emphasized series and passing to TooltipContent
  const sortedTooltipSeries = useMemo(() => {
    return tooltipSeries.length > 0
      ? sortTooltipSeries({ series: tooltipSeries, tooltipSort: tooltipConfig.sort, isStacked })
      : EMPTY_TOOLTIP_DATA;
  }, [tooltipSeries, tooltipConfig.sort, isStacked]);

  if (!shouldRenderTooltip || tooltipSeries.length === 0) {
    return null;
  }

  // Check whether to extract emphasized series to render it separately in a static section
  const extractedEmphasizedSeries = getEmphasizedSeriesInfo({
    sortedSeries: sortedTooltipSeries,
  });

  // Allows tooltip pinning to work even when panel container has overflow hidden set (see sc-58302).
  // When pinning is disabled, the tooltip can attach to the DOM body instead of requiring tooltipPortalId to be set.
  const effectiveContainerElement = containerElement && enablePinning ? containerElement : undefined;

  if (!isTooltipPinned) {
    transform.current = assembleTransform({
      mousePos,
      pinnedPos,
      tooltipHeight: height ?? 0,
      tooltipWidth: width ?? 0,
      containerElement: effectiveContainerElement,
    });
  }

  // Used for event grouping and histogram buckets, tooltip can be narrower when false.
  const isEventTooltip = isEvent(tooltipSeries);
  const { showEndTime } = tooltipConfig;
  const isLargeWidthEnabled = !!showEndTime || isEventTooltip;

  // Used to determine which tooltip mode to toggle to when button is clicked in tooltip footer.
  const isShowingAll =
    maxNearbySeries === TIME_SERIES_TOOLTIP_MODE_SERIES_LIMIT || tooltipSeries.length === totalSeries;

  const showSeriesPaginationInfo = !isEventTooltip;
  const showTooltipFooter = showSeriesPaginationInfo || tooltipConfig.showTotal;

  let menuItemsActions: SeriesMenuItemOptions[] = [];
  if (tooltipConfig.tooltipContextMenuProps?.menuItems) {
    const firstSeries = tooltipSeries[0]; // Get the timestamp for the first series to pass to menu items.
    menuItemsActions = tooltipConfig.tooltipContextMenuProps?.menuItems({
      timestamp: firstSeries?.date,
      isTooltipPinned,
    });
  }
  const showContextMenuItems = menuItemsActions.length > 0 && isTooltipPinned;

  return (
    <Portal container={effectiveContainerElement}>
      <Box
        data-testid="time-chart-tooltip"
        ref={tooltipRef}
        sx={(theme) =>
          getTooltipStyles(theme, {
            maxHeight: dynamicMaxHeight,
            isLargeWidthEnabled,
            containerElement: effectiveContainerElement,
          })
        }
        style={{
          transform: transform.current,
          display: transform.current ? 'flex' : 'none',
        }}
        // You should be able to click on the tooltip without it closing.
        onDoubleClick={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <TooltipLayout>
          <TooltipStaticContentWrapper>
            <TimeSeriesTooltipHeader
              series={tooltipSeries}
              dataset={dataset}
              enablePinning={enablePinning}
              isTooltipPinned={isTooltipPinned}
              onUnpinClick={onUnpinClick}
              timeRange={timeRange}
              showEndTime={showEndTime}
              isEventTooltip={isEventTooltip}
              timeScale={timeScale}
            />
          </TooltipStaticContentWrapper>
          <CommonLabelsSection
            commonLabels={tooltipConfig.commonLabels}
            defaultExpanded={tooltipConfig.expandCommonLabels}
          />
          <TooltipDynamicContentWrapper>
            <TooltipContent
              isTooltipPinned={isTooltipPinned}
              series={sortedTooltipSeries}
              tooltipConfig={tooltipConfig}
              totalSeries={totalSeries}
              isStacked={isStacked}
              unit={adjustedUnit}
              clearPinned={onUnpinClick}
              excludeEmphasized={!!extractedEmphasizedSeries}
            />
          </TooltipDynamicContentWrapper>

          {extractedEmphasizedSeries && (
            <TooltipStaticContentWrapper>
              <EmphasizedSeriesSection
                emphasizedSeries={extractedEmphasizedSeries}
                isTooltipPinned={isTooltipPinned}
                series={tooltipSeries}
                tooltipConfig={tooltipConfig}
                totalSeries={totalSeries}
                isStacked={isStacked}
                unit={adjustedUnit}
                clearPinned={onUnpinClick}
                onIncreaseHeight={() => setDynamicMaxHeight((prev) => prev + TOOLTIP_HEIGHT_INCREASE_INCREMENT)}
              />
            </TooltipStaticContentWrapper>
          )}

          {showTooltipFooter && (
            <TimeSeriesTooltipFooter
              tooltipSeries={tooltipSeries}
              totalSeries={totalSeries}
              total={total}
              unit={adjustedUnit}
              mode={tooltipConfig.mode}
              maxNearbySeries={tooltipConfig.maxNearbySeries}
              showSeriesPaginationInfo={showSeriesPaginationInfo}
              showTotal={tooltipConfig.showTotal}
              isStacked={isStacked}
              isShowingAll={isShowingAll}
              onToggleTooltipMode={() => toggleTooltipMode(isShowingAll)}
            />
          )}
          {showContextMenuItems && !isEventTooltip && (
            <TooltipStaticContentWrapper>
              <TooltipContextMenu menuItemsActions={menuItemsActions} clearPinned={onUnpinClick} />
            </TooltipStaticContentWrapper>
          )}
        </TooltipLayout>
      </Box>
    </Portal>
  );
});

function useUpdateTooltipMode({
  initialMaxNearbySeries = DEFAULT_MAX_NEARBY_SERIES,
  mode,
}: {
  initialMaxNearbySeries?: number;
  mode?: TooltipMode;
}) {
  const [maxNearbySeries, setMaxNearbySeries] = useState(initialMaxNearbySeries);

  // Keep in sync when tooltipConfig updates (ex: when tooltip mode changed in panel settings)
  useEffect(() => {
    setMaxNearbySeries(initialMaxNearbySeries);
  }, [initialMaxNearbySeries]);

  const toggleTooltipMode = useCallback(
    (isShowingAll: boolean) => {
      // If 'Show single' or 'Show less' is clicked, switch to back to correct number of series for that mode.
      // Otherwise, show up to series limit (ex: 500 series) since showing all would degrade performance.
      const resetSeriesCount = mode === 'single' ? 1 : initialMaxNearbySeries;
      setMaxNearbySeries(isShowingAll ? resetSeriesCount : TIME_SERIES_TOOLTIP_MODE_SERIES_LIMIT);
    },
    [mode, initialMaxNearbySeries]
  );

  return { maxNearbySeries, toggleTooltipMode };
}
